import React, { useState } from 'react';
import { ConfigDocument, PressConfig } from '../../types';
import { allStop } from '../../services/configApi';

interface PressConfigTabProps {
  config: ConfigDocument | null;
  onSave: (config: ConfigDocument) => Promise<void>;
  onReload: () => Promise<void>;
  loading: boolean;
}

const PRESS_NAMES = ['CLE1', 'DFW1', 'HTD1', 'LAS1', 'MKE1', 'MKE2', 'ORL1'];

/**
 * Detect if setting `pressName` to redirect to `targetPress` would create
 * a circular chain, given the current presses config (with the proposed change applied).
 */
function wouldCreateCircularRedirect(
  presses: Record<string, PressConfig>,
  pressName: string,
  targetPress: string | null,
): boolean {
  if (!targetPress) return false;

  // Simulate the proposed change
  const simulated: Record<string, string | null> = {};
  for (const [name, cfg] of Object.entries(presses)) {
    simulated[name] = name === pressName ? targetPress : cfg.redirectTo;
  }

  // Follow the chain from targetPress — if we reach pressName, it's circular
  let current: string | null = targetPress;
  const visited = new Set<string>();
  while (current) {
    if (current === pressName) return true;
    if (visited.has(current)) return false; // cycle that doesn't include pressName
    visited.add(current);
    current = simulated[current] ?? null;
  }
  return false;
}

const PressConfigTab: React.FC<PressConfigTabProps> = ({ config, onSave, onReload, loading }) => {
  const [editingPress, setEditingPress] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState<'active' | 'paused'>('active');
  const [editSmbDest, setEditSmbDest] = useState('');
  const [editRedirect, setEditRedirect] = useState<string | null>(null);
  const [editMaxConcurrent, setEditMaxConcurrent] = useState<number>(5);
  const [saving, setSaving] = useState(false);

  // ALL STOP state
  const [showAllStopConfirm, setShowAllStopConfirm] = useState(false);
  const [allStopLoading, setAllStopLoading] = useState(false);
  const [allStopBanner, setAllStopBanner] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const presses: [string, PressConfig][] = config
    ? PRESS_NAMES.filter((name) => name in config.presses).map((name) => [name, config.presses[name]])
    : [];

  const smbDestinationKeys = config ? Object.keys(config.smbDestinations).sort((a, b) => a.localeCompare(b)) : [];

  const handleEdit = (pressName: string) => {
    const press = config?.presses[pressName];
    if (!press) return;
    setEditingPress(pressName);
    setEditStatus(press.status);
    setEditSmbDest(press.smbDestination);
    setEditRedirect(press.redirectTo);
    setEditMaxConcurrent(press.maxConcurrentJobs ?? 5);
    setAllStopBanner(null);
  };

  const handleCancel = () => {
    setEditingPress(null);
  };

  const handleSave = async () => {
    if (!config || !editingPress) return;

    setSaving(true);
    try {
      const updatedPresses = { ...config.presses };
      updatedPresses[editingPress] = {
        status: editStatus,
        smbDestination: editSmbDest,
        redirectTo: editRedirect || null,
        maxConcurrentJobs: editMaxConcurrent,
      };

      await onSave({
        ...config,
        presses: updatedPresses,
      });

      setEditingPress(null);
    } finally {
      setSaving(false);
    }
  };

  const handleAllStop = async () => {
    setShowAllStopConfirm(false);
    setAllStopLoading(true);
    setAllStopBanner(null);

    try {
      const result = await allStop();
      // The allStop endpoint returns { success, message, presses } or { success: false, message, failedPresses }
      if (result && (result as any).success === false) {
        const failedPresses = (result as any).failedPresses || [];
        setAllStopBanner({
          type: 'error',
          message: `ALL STOP partially failed. Presses ${failedPresses.join(', ')} could not be paused.`,
        });
      } else {
        setAllStopBanner({
          type: 'success',
          message: 'ALL STOP executed — all presses have been paused.',
        });
        // Reload config from server to pick up the new version
        // (all-stop already incremented the version in DynamoDB)
        await onReload();
      }
    } catch (err: any) {
      setAllStopBanner({
        type: 'error',
        message: err.message || 'ALL STOP operation failed. Please try again.',
      });
    } finally {
      setAllStopLoading(false);
    }
  };

  /**
   * Get redirect options for a press, excluding itself and any target
   * that would create a circular chain.
   */
  const getRedirectOptions = (pressName: string): string[] => {
    if (!config) return [];
    return PRESS_NAMES.filter((name) => {
      if (name === pressName) return false;
      if (!(name in config.presses)) return false;
      return !wouldCreateCircularRedirect(config.presses, pressName, name);
    });
  };

  if (loading && !config) {
    return <div className="text-gray-500">Loading configuration...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header with ALL STOP button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Press Configuration</h3>
        <button
          type="button"
          onClick={() => { setShowAllStopConfirm(true); setAllStopBanner(null); }}
          disabled={allStopLoading || !config}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-bold rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {allStopLoading ? 'Stopping...' : '⛔ ALL STOP'}
        </button>
      </div>

      {/* ALL STOP confirmation dialog */}
      {showAllStopConfirm && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-4 rounded-md" role="alert">
          <p className="text-sm font-medium">This will pause ALL presses immediately. Are you sure?</p>
          <div className="mt-3 flex space-x-3">
            <button
              type="button"
              onClick={handleAllStop}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
            >
              Confirm ALL STOP
            </button>
            <button
              type="button"
              onClick={() => setShowAllStopConfirm(false)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ALL STOP result banner */}
      {allStopBanner && (
        <div
          className={`p-3 rounded-md flex items-center space-x-2 ${
            allStopBanner.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}
          role="alert"
        >
          <span className="text-lg">{allStopBanner.type === 'success' ? '✓' : '✗'}</span>
          <span className="text-sm">{allStopBanner.message}</span>
        </div>
      )}

      {/* Presses Table */}
      {presses.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6 text-center text-gray-500">
          No presses configured.
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Press
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SMB Destination
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Redirect Target
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Max Jobs
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {presses.map(([name, press]) => (
                <React.Fragment key={name}>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          press.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {press.status === 'active' ? 'Active' : 'Paused'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {press.smbDestination || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {press.redirectTo || 'None'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {press.maxConcurrentJobs ?? 5}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        type="button"
                        onClick={() => handleEdit(name)}
                        disabled={editingPress !== null}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                  {editingPress === name && config && (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 bg-blue-50">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <label htmlFor="press-status" className="block text-sm font-medium text-gray-700">Status</label>
                            <select id="press-status" value={editStatus} onChange={(e) => setEditStatus(e.target.value as 'active' | 'paused')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                              <option value="active">Active</option>
                              <option value="paused">Paused</option>
                            </select>
                          </div>
                          <div>
                            <label htmlFor="press-smb" className="block text-sm font-medium text-gray-700">SMB Destination</label>
                            <select id="press-smb" value={editSmbDest} onChange={(e) => setEditSmbDest(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                              <option value="">Select destination...</option>
                              {smbDestinationKeys.map((key) => (<option key={key} value={key}>{key}</option>))}
                            </select>
                          </div>
                          <div>
                            <label htmlFor="press-redirect" className="block text-sm font-medium text-gray-700">Redirect Target</label>
                            <select id="press-redirect" value={editRedirect || ''} onChange={(e) => setEditRedirect(e.target.value || null)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                              <option value="">None</option>
                              {getRedirectOptions(name).map((n) => (<option key={n} value={n}>{n}</option>))}
                            </select>
                          </div>
                          <div>
                            <label htmlFor="press-max-concurrent" className="block text-sm font-medium text-gray-700">Max Concurrent Jobs</label>
                            <input id="press-max-concurrent" type="number" min={1} max={50} value={editMaxConcurrent} onChange={(e) => setEditMaxConcurrent(Math.max(1, parseInt(e.target.value) || 1))} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm" />
                          </div>
                        </div>
                        <div className="mt-3 flex space-x-3">
                          <button type="button" onClick={handleSave} disabled={saving} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">{saving ? 'Saving...' : 'Save'}</button>
                          <button type="button" onClick={handleCancel} disabled={saving} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PressConfigTab;
